import React, { useEffect, useState } from 'react'
import { Button, Modal, Space, Spin, Typography } from 'antd'
import LeftOutlined from '@ant-design/icons/LeftOutlined'
import RightOutlined from '@ant-design/icons/RightOutlined'
import CheckOutlined from '@ant-design/icons/CheckOutlined'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import { GQL_CLIENT } from 'src/graphqlClient'
import { getFileDownloadUrl } from '@/features/file-upload/services/ProjectFileService'
import type { FileView } from '../types/documents.types'

/** The modal only needs identity, a display name and a mime type to render. */
export type PreviewableFile = Pick<FileView, 'id' | 'name' | 'mime_type'>

type Props = {
  file: PreviewableFile | null
  open: boolean
  onClose: () => void
  // --- Optional sibling navigation ---------------------------------------
  // When provided, previous/next controls render in the footer so the
  // reviewer can page through a group of files without closing the modal.
  // Left/Right arrow keys are wired to these while the modal is open.
  onPrev?: () => void
  onNext?: () => void
  hasPrev?: boolean
  hasNext?: boolean
  // Free-text position indicator, e.g. "2 / 7".
  positionLabel?: string
  // --- Optional inline validation ----------------------------------------
  // When `canValidate` is true and the callbacks are provided, "Valider" /
  // "Rejeter" buttons render so the file can be accepted/rejected straight
  // from the preview. The parent decides whether validation is allowed.
  canValidate?: boolean
  onAccept?: () => void
  onReject?: () => void
  actionLoading?: boolean
}

export const FilePreviewModal: React.FC<Props> = ({
  file,
  open,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  positionLabel,
  canValidate = false,
  onAccept,
  onReject,
  actionLoading = false,
}) => {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !file) {
      setUrl(null)
      setError(null)
      return
    }
    let cancelled = false
    setUrl(null)
    setError(null)
    getFileDownloadUrl(GQL_CLIENT, file.id, 'original')
      .then(({ downloadUrl }) => {
        if (!cancelled) setUrl(downloadUrl)
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message ?? 'Erreur lors du chargement du fichier.')
      })
    return () => {
      cancelled = true
    }
  }, [open, file])

  // Left/Right arrow keys page through siblings while the modal is open.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrev) onPrev?.()
      if (e.key === 'ArrowRight' && hasNext) onNext?.()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, hasPrev, hasNext, onPrev, onNext])

  const body = (() => {
    if (!file) return null
    if (error) {
      return (
        <Typography.Text type="danger" data-cy="file-preview-error">
          {error}
        </Typography.Text>
      )
    }
    if (!url) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin data-cy="file-preview-loading" />
        </div>
      )
    }
    if (file.mime_type?.startsWith('image/')) {
      return (
        <img
          src={url}
          alt={file.name}
          style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
        />
      )
    }
    if (file.mime_type === 'application/pdf') {
      return (
        <iframe
          src={url}
          title={file.name}
          style={{ width: '100%', height: '70vh', border: 0 }}
        />
      )
    }
    return (
      <Typography.Paragraph>
        Aperçu indisponible pour ce type de fichier.{' '}
        <a href={url} target="_blank" rel="noopener noreferrer">
          Télécharger le fichier
        </a>
        .
      </Typography.Paragraph>
    )
  })()

  const hasNav = Boolean(onPrev || onNext)
  const hasValidation = canValidate && Boolean(onAccept || onReject)
  const footer =
    hasNav || hasValidation ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <Space>
          {hasNav && (
            <>
              <Button
                icon={<LeftOutlined />}
                disabled={!hasPrev}
                onClick={onPrev}
                data-cy="file-preview-prev"
              >
                Précédent
              </Button>
              <Button
                disabled={!hasNext}
                onClick={onNext}
                data-cy="file-preview-next"
              >
                Suivant <RightOutlined />
              </Button>
            </>
          )}
          {positionLabel && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {positionLabel}
            </Typography.Text>
          )}
        </Space>
        {hasValidation && (
          <Space>
            <Button
              danger
              icon={<CloseOutlined />}
              loading={actionLoading}
              onClick={onReject}
              data-cy="file-preview-reject"
            >
              Rejeter
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={actionLoading}
              onClick={onAccept}
              data-cy="file-preview-accept"
            >
              Valider
            </Button>
          </Space>
        )}
      </div>
    ) : null

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={file?.name}
      footer={footer}
      width={900}
      destroyOnClose
      data-cy="file-preview-modal"
      getContainer={false}
    >
      {body}
    </Modal>
  )
}
